import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

export interface PickOption {
    id: string;
    label: string;
    sublabel?: string;
    /** Arbitrary extra data the caller can read back via `onPick` (e.g. a
     * site's owning customer), without widening the picker's value type. */
    meta?: Record<string, unknown>;
}

/** Debounce a changing value so search inputs don't fire a request per keystroke. */
export function useDebouncedValue<T>(value: T, ms: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return debounced;
}

/**
 * Searchable picker backed by an authed list endpoint. Resolves the label for
 * an already-selected id (so a stored UUID shows a human name), and lets the
 * user search + pick a new one. Used for customer / site / estimate matching
 * in the work-order review surfaces instead of raw UUID text boxes.
 */
export function AsyncPicker({
    value,
    onChange,
    onPick,
    placeholder,
    disabled,
    disabledHint,
    queryKeyBase,
    buildSearchPath,
    mapResults,
    buildResolvePath,
    mapResolvedLabel,
}: {
    value: string | null;
    onChange: (id: string | null) => void;
    /** Fires with the full option when the user picks a result (not on clear /
     * resolve), so callers can read `meta` to e.g. set a linked field. */
    onPick?: (opt: PickOption) => void;
    placeholder: string;
    disabled?: boolean;
    disabledHint?: string;
    queryKeyBase: string;
    buildSearchPath: (q: string) => string;
    mapResults: (data: unknown) => PickOption[];
    buildResolvePath: (id: string) => string;
    mapResolvedLabel: (data: unknown) => string;
}) {
    const [editing, setEditing] = useState(false);
    const [q, setQ] = useState("");
    const debounced = useDebouncedValue(q, 250);

    const resultsQ = useQuery({
        queryKey: [queryKeyBase, "search", debounced],
        queryFn: async () => mapResults(await api.get(buildSearchPath(debounced))),
        // Search whenever the field is active: either the user clicked "Change"
        // on an existing selection (editing) OR there's no value yet so the bare
        // search box is showing. Without the `!value` case the box renders but
        // never fires the request, so it shows a false "No matches".
        enabled: (editing || !value) && !disabled,
    });

    const selectedQ = useQuery({
        queryKey: [queryKeyBase, "resolve", value],
        queryFn: async () => mapResolvedLabel(await api.get(buildResolvePath(value as string))),
        enabled: !!value,
    });

    if (disabled) {
        return <p className="text-xs text-foreground/60">{disabledHint ?? placeholder}</p>;
    }

    if (value && !editing) {
        return (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5">
                <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <span className="min-w-0 flex-1 truncate text-sm">
                    {selectedQ.isLoading ? "Loading…" : (selectedQ.data ?? value)}
                </span>
                <button
                    type="button"
                    className="text-xs font-medium text-primary hover:underline"
                    onClick={() => {
                        setQ("");
                        setEditing(true);
                    }}
                >
                    Change
                </button>
                <button
                    type="button"
                    aria-label="Clear"
                    className="text-foreground/50 hover:text-foreground"
                    onClick={() => onChange(null)}
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
        );
    }

    const results = resultsQ.data ?? [];
    return (
        <div className="space-y-1.5">
            <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-foreground/40" />
                <Input
                    autoFocus={editing}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={placeholder}
                    className="pl-7"
                />
                {value && (
                    <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-foreground/60 hover:text-foreground"
                        onClick={() => setEditing(false)}
                    >
                        Cancel
                    </button>
                )}
            </div>
            {(resultsQ.isLoading || results.length > 0) && (
                <div className="max-h-44 overflow-y-auto rounded-md border bg-card">
                    {resultsQ.isLoading ? (
                        <p className="px-2.5 py-2 text-xs text-foreground/60">Searching…</p>
                    ) : (
                        results.map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                className="flex w-full flex-col items-start gap-0.5 px-2.5 py-1.5 text-left text-sm hover:bg-primary/5"
                                onClick={() => {
                                    onChange(opt.id);
                                    onPick?.(opt);
                                    setEditing(false);
                                    setQ("");
                                }}
                            >
                                <span className="truncate font-medium">{opt.label}</span>
                                {opt.sublabel && (
                                    <span className="truncate text-xs text-foreground/60">{opt.sublabel}</span>
                                )}
                            </button>
                        ))
                    )}
                </div>
            )}
            {!resultsQ.isLoading && debounced.trim() && results.length === 0 && (
                <p className="px-1 text-xs text-foreground/60">No matches.</p>
            )}
        </div>
    );
}
